import { Close } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import { EModal } from '../../@types/Enums';
import { IconRoomForm1, IconRoomForm2, IconRoomForm3, IconRoomForm4 } from '../../assets/icons';
import { useModalContext } from '../../provider/ModalProvider';
import { SAMPLE_ROOM_1_ID, SAMPLE_ROOM_2_ID, SAMPLE_ROOM_3_ID, SAMPLE_ROOM_4_ID, SampleRoomId } from '../../utils/constants';

type RoomShapeOption = {
  id: SampleRoomId;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const roomShapeOptions: RoomShapeOption[] = [
  { id: SAMPLE_ROOM_1_ID, Icon: IconRoomForm1 },
  { id: SAMPLE_ROOM_2_ID, Icon: IconRoomForm2 },
  { id: SAMPLE_ROOM_3_ID, Icon: IconRoomForm3 },
  { id: SAMPLE_ROOM_4_ID, Icon: IconRoomForm4 },
];

const styles = {
  root: {
    width: '100%',
    borderRadius: '14px',
    bgcolor: '#fff',
    color: '#444',
    fontFamily: 'Roboto',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 56,
    px: 2,
    borderBottom: '1px solid #d8d8d8',
  },
  title: {
    fontFamily: 'Roboto',
    fontSize: '24px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  selector: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: { xs: 2, sm: 4 },
    px: { xs: 3, sm: 6 },
    py: { xs: 3, sm: 4.5 },
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1.5,
    border: 0,
    background: 'transparent',
    color: '#444',
    cursor: 'pointer',
    p: 0,
    fontFamily: 'Roboto',
  },
  iconFrame: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: { xs: 78, sm: 104 },
    height: { xs: 78, sm: 104 },
    borderRadius: '14px',
    color: '#9d9d9d',
    '& svg': {
      display: 'block',
    },
    '&:hover': {
      bgcolor: '#f1f1f1',
      color: '#444',
      boxShadow: 'inset 0 0 0 1px #dedede',
    },
  },
  label: {
    fontFamily: 'Roboto',
    fontSize: { xs: '18px', sm: '24px' },
    fontWeight: 400,
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
  },
};

const getRoomShapeLabel = (intl: IntlShape, id: SampleRoomId) =>
  intl.formatMessage({ id: 'SelectRoomSizeModal.roomShapeLabel', defaultMessage: 'Raumform {number}' }, { number: id });

const getDefaultRoomName = (intl: IntlShape, id: SampleRoomId) =>
  intl.formatMessage({ id: 'SelectRoomSizeModal.defaultRoomName', defaultMessage: 'Raum {number}' }, { number: id });

const PredefinedRoomsModal = () => {
  const intl = useIntl();
  const { setActiveModal, setIsModalShown, setSelectedRoomName, setSelectedRoomId } = useModalContext();

  const handleRoomSelection = (roomId: SampleRoomId) => {
    const roomName = getDefaultRoomName(intl, roomId);

    setSelectedRoomId(roomId);
    setSelectedRoomName(roomName);
    setActiveModal(EModal.SELECT_ROOM_SIZE);
  };

  return (
    <Box component="div" id="modalBox" sx={styles.root}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{intl.formatMessage({ id: 'PredefinedRoomsModal.title', defaultMessage: 'Wähle eine Raumform' })}</Typography>
        <IconButton aria-label={intl.formatMessage({ id: 'aria.close', defaultMessage: 'Close' })} onClick={() => setIsModalShown(false)} sx={{ color: '#444', p: 0.5 }}>
          <Close />
        </IconButton>
      </Box>

      <Box sx={styles.selector} aria-label={intl.formatMessage({ id: 'PredefinedRoomsModal.chooseRoomShape', defaultMessage: 'Raumform wählen' })}>
        {roomShapeOptions.map(({ id, Icon }) => (
          <Box key={id} component="button" type="button" onClick={() => handleRoomSelection(id)} sx={styles.option}>
            <Box sx={styles.iconFrame}>
              <Icon width="68%" height="68%" />
            </Box>
            <Typography sx={styles.label}>{getRoomShapeLabel(intl, id)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PredefinedRoomsModal;
